import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaTruck, FaMapMarkerAlt, FaUser, FaWeight, FaList,
  FaClipboardList, FaCheckCircle, FaBuilding
} from 'react-icons/fa';
import { shipmentsService } from '../services/shipmentsService';
import VehicleTypeSelector from '../components/VehicleTypeSelector.jsx';
import AdminClearanceTabs from '../components/AdminClearanceTabs.jsx'
import LocationSelect from '../components/LocationSelect.jsx';
import LocationMapSelector from '../components/LocationMapSelector.jsx';

const AdminCreateShipment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [clearanceResponse, setClearanceResponse] = useState(null)

  // Map selector state
  const [mapSelectorOpen, setMapSelectorOpen] = useState({ pickup: false, drop: false });
  const [mapSelectorType, setMapSelectorType] = useState('pickup');

  // Date display states (dd/mm/yyyy)
  const [bookingDateDisplay, setBookingDateDisplay] = useState('');
  const [deliveryDateDisplay, setDeliveryDateDisplay] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    companyName: '',
    pickupLocation: '',
    dropLocation: '',
    cargoType: '',
    description: '',
    vehicleType: '',
    customVehicleType: '',
    cargoWeight: '',
    numberOfVehicles: '',
    bookingDate: '',
    deliveryDate: '',
    salesTax: false,
    platform: 'web',
  });
  const [mode, setMode] = useState('shipment') // 'shipment' or 'clearance'
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const today = new Date();
    const dd = String(today.getDate()).padStart(2, '0');
    const mm = String(today.getMonth() + 1).padStart(2, '0');
    const yyyy = today.getFullYear();
    const formatted = `${dd}/${mm}/${yyyy}`;
    setBookingDateDisplay(formatted);

    const iso = `${yyyy}-${mm}-${dd}`;
    setFormData(prev => ({ ...prev, bookingDate: iso }));
  }, []);

  const formatDateInput = (value) => {
    const digits = value.replace(/[^\d/]/g, '');
    if (digits.length < value.length) return digits;

    const numbers = digits.replace(/\D/g, '');
    if (numbers.length === 1) return numbers;

    const limited = numbers.slice(0, 8);
    let day = limited.slice(0, 2);
    let month = limited.slice(2, 4);
    let year = limited.slice(4, 8);

    if (day.length === 2 && parseInt(day) > 31) day = '31';
    if (day.length === 2 && parseInt(day) < 1) day = '01';
    if (month.length === 2 && parseInt(month) > 12) month = '12';
    if (month.length === 2 && parseInt(month) < 1) month = '01';

    const currentYear = new Date().getFullYear();
    const minYear = currentYear;
    const maxYear = currentYear + 2;
    if (year && year.length === 4) {
      const parsedYear = parseInt(year);
      if (parsedYear < minYear || parsedYear > maxYear) {
        year = String(currentYear);
      }
    }

    let formatted = day;
    if (day.length === 2) {
      formatted += '/';
      if (month) formatted += month;
      if (month.length === 2) {
        formatted += '/';
        if (year) formatted += year;
      }
    }
    return formatted;
  };

  const validateAndConvertDate = (formatted, key) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formatted);
    if (match) {
      const [, day, month, year] = match;
      const d = parseInt(day, 10);
      const m = parseInt(month, 10);
      const y = parseInt(year, 10);
      if (m >= 1 && m <= 12 && d >= 1 && d <= 31) {
        const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        setFormData(prev => ({ ...prev, [key]: iso }));
        return;
      }
    }
    setFormData(prev => ({ ...prev, [key]: '' }));
  };

  const handleBookingDateChange = () => { };

  const handleDeliveryDateChange = (e) => {
    const formatted = formatDateInput(e.target.value);
    setDeliveryDateDisplay(formatted);
    validateAndConvertDate(formatted, 'deliveryDate');

    const deliveryISO = convertFormattedToISO(formatted);
    if (deliveryISO && formData.bookingDate) {
      const bDate = new Date(formData.bookingDate);
      const dDate = new Date(deliveryISO);
      if (dDate < bDate) {
        setErrors(prev => ({
          ...prev,
          deliveryDate: 'Delivery date cannot be before booking date',
        }));
      } else {
        setErrors(prev => ({ ...prev, deliveryDate: '' }));
      }
    }
  };

  const convertFormattedToISO = (formatted) => {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(formatted);
    if (!match) return null;
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleLocationChange = (value, name) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleOpenMapSelector = (type) => {
    setMapSelectorType(type);
    setMapSelectorOpen({ pickup: type === 'pickup', drop: type === 'drop' });
  };

  const handleCloseMapSelector = () => {
    setMapSelectorOpen({ pickup: false, drop: false });
  };

  const handleMapLocationSelect = (locationData) => {
    const fieldName = mapSelectorType === 'pickup' ? 'pickupLocation' : 'dropLocation';
    handleLocationChange(locationData.address, fieldName);
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData(prev => ({
      ...prev,
      vehicleType: vehicle.name,
      customVehicleType: '',
    }));
    if (errors.vehicleType) setErrors(prev => ({ ...prev, vehicleType: '' }));
  };

  const handleOtherClick = () => {
    if (formData.vehicleType === 'other') {
      setSelectedVehicle(null);
      setFormData(prev => ({
        ...prev,
        vehicleType: '',
        customVehicleType: '',
      }));
    } else {
      setSelectedVehicle(null);
      setFormData(prev => ({
        ...prev,
        vehicleType: 'other',
        customVehicleType: '',
      }));
    }
    if (errors.vehicleType) setErrors(prev => ({ ...prev, vehicleType: '' }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.company.trim()) newErrors.company = 'Company is required';

    if (!formData.pickupLocation.trim()) newErrors.pickupLocation = 'Please enter pickup location';
    if (!formData.dropLocation.trim()) newErrors.dropLocation = 'Please enter delivery location';
    if (
      formData.pickupLocation.trim() &&
      formData.dropLocation.trim() &&
      formData.pickupLocation.trim() === formData.dropLocation.trim()
    ) {
      newErrors.dropLocation = 'Delivery location must be different from pickup location';
    }
    if (formData.pickupLocation && formData.pickupLocation.trim().length < 5) {
      newErrors.pickupLocation = 'Pickup location must be at least 5 characters';
    }
    if (formData.dropLocation && formData.dropLocation.trim().length < 5) {
      newErrors.dropLocation = 'Drop location must be at least 5 characters';
    }

    if (!formData.vehicleType) newErrors.vehicleType = 'Vehicle type is required';
    if (formData.vehicleType === 'other' && !formData.customVehicleType.trim()) {
      newErrors.customVehicleType = 'Please specify vehicle type';
    }

    if (!formData.cargoType.trim()) newErrors.cargoType = 'Cargo type is required';
    if (!formData.cargoWeight) newErrors.cargoWeight = 'Cargo weight is required';
    else if (isNaN(formData.cargoWeight) || formData.cargoWeight <= 0) {
      newErrors.cargoWeight = 'Valid weight required';
    }
    if (!formData.numberOfVehicles) newErrors.numberOfVehicles = 'Number of vehicles required';
    else if (isNaN(formData.numberOfVehicles) || formData.numberOfVehicles < 1) {
      newErrors.numberOfVehicles = 'Must be at least 1';
    }

    if (!formData.bookingDate) newErrors.bookingDate = 'Booking date is required';
    if (!formData.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (formData.bookingDate && formData.deliveryDate) {
      const bDate = new Date(formData.bookingDate);
      const dDate = new Date(formData.deliveryDate);
      if (dDate < bDate) {
        newErrors.deliveryDate = 'Delivery date cannot be before booking date';
      }
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors in the form');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        companyName: formData.companyName || undefined,
        pickupLocation: formData.pickupLocation,
        dropLocation: formData.dropLocation,
        cargoType: formData.cargoType,
        description: formData.description || undefined,
        vehicleType: formData.vehicleType === 'other' ? formData.customVehicleType : formData.vehicleType,
        cargoWeight: formData.cargoWeight ? Number(formData.cargoWeight) : undefined,
        numberOfVehicles: formData.numberOfVehicles ? Number(formData.numberOfVehicles) : undefined,
        bookingDate: formData.bookingDate,
        deliveryDate: formData.deliveryDate || undefined,
        salesTax: formData.salesTax || undefined,
        platform: 'web',
      };

      const response = await shipmentsService.createShipmentWithUser(payload);
      console.log('API Response:', response);

      if (response.success) {
        toast.success('Shipment created successfully!');
        const { userCreated, tempPassword } = response.data || {};

        // If a temp password is returned, show it (new user created)
        if (tempPassword) {
          setTempPassword(tempPassword);
          setShowPasswordModal(true);
        }
        // If user was created but no password (backend missing env var)
        else if (userCreated) {
          setTempPassword('(Default password – contact admin for credentials)');
          setShowPasswordModal(true);
        }
        // Existing user, go to orders list
        else {
          navigate('/orders');
        }
      } else {
        toast.error(response.message || 'Failed to create shipment');
      }
    } catch (error) {
      toast.error('An error occurred while creating shipment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
      <div className="max-w-4xl">
        <div className="pb-5 mb-8 border-b border-[#e9ecef]">
          <div className="flex items-center gap-3 mb-1">
            <FaTruck className="text-[#3498db] text-xl" />
            <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Create New Shipment</h1>
          </div>
          <p className="text-sm text-[#7f8c8d]">Fill out the details below to create a shipment for your client</p>
        </div>

        {/* Mode Toggle */}
        <div className="mb-6">
          <div className="inline-flex rounded-lg bg-[#f8f9fa] border border-[#e9ecef] p-1 gap-1" role="tablist">
            <button
              type="button"
              onClick={() => setMode('shipment')}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'shipment' ? 'bg-[#2c3e50] text-white' : 'text-[#7f8c8d] hover:text-[#2c3e50]'}`}
            >
              Create Shipment
            </button>
            <button
              type="button"
              onClick={() => setMode('clearance')}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${mode === 'clearance' ? 'bg-[#2c3e50] text-white' : 'text-[#7f8c8d] hover:text-[#2c3e50]'}`}
            >
              Create Clearance
            </button>
          </div>
        </div>

        {mode === 'shipment' ? (
          <form onSubmit={handleSubmit} className="space-y-5">
          {/* Customer Information */}
          <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-2">
              <FaUser className="text-[#3498db]" />
              <h2 className="text-base font-semibold text-[#2c3e50]">Customer Information</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Full Name *</label>
                  <input type="text" name="name" value={formData.name} placeholder="Enter your full name" onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.name ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                  {errors.name && <p className="text-[#e74c3c] text-xs mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Email</label>
                  <input type="email" name="email" value={formData.email} placeholder="Enter your email" onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.email ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                  {errors.email && <p className="text-[#e74c3c] text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Phone *</label>
                  <input type="text" name="phone" value={formData.phone} placeholder="Enter your phone number" onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.phone ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                  {errors.phone && <p className="text-[#e74c3c] text-xs mt-1">{errors.phone}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Company *</label>
                  <input type="text" name="company" value={formData.company} placeholder="Enter company name" onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.company ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                  {errors.company && <p className="text-[#e74c3c] text-xs mt-1">{errors.company}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Shipment Details */}
          <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-hidden">
            <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-2">
              <FaClipboardList className="text-[#3498db]" />
              <h2 className="text-base font-semibold text-[#2c3e50]">Shipment Details</h2>
            </div>
            <div className="p-6 space-y-7">
              {/* Vehicle Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#2c3e50] flex items-center gap-2 mb-4">
                  <FaTruck className="text-[#3498db]" /> Vehicle Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Vehicle Type *</label>
                    <div className="flex flex-wrap gap-2">
                      {['Suzuki', 'Shehzore', 'Mazda', 'Truck', 'Trailer'].map((cat) => (
                        <button key={cat} type="button"
                          onClick={() => { setSelectedCategory(cat); setSelectorOpen(true); }}
                          className="px-4 py-2 border border-[#e9ecef] rounded-lg text-sm text-[#7f8c8d] hover:border-[#3498db] hover:text-[#3498db] transition-colors">
                          {cat}
                        </button>
                      ))}
                      <button type="button" onClick={handleOtherClick}
                        className={`px-4 py-2 border rounded-lg text-sm transition-colors ${formData.vehicleType === 'other'
                          ? 'border-[#3498db] bg-[#eaf4fb] text-[#3498db]'
                          : 'border-[#e9ecef] text-[#7f8c8d] hover:border-[#3498db] hover:text-[#3498db]'
                        }`}>
                        Other
                      </button>
                    </div>
                    {errors.vehicleType && <p className="text-[#e74c3c] text-xs mt-1">{errors.vehicleType}</p>}
                  </div>

                  {selectedVehicle && (
                    <div className="md:col-span-2 mt-1 p-4 bg-[#eaf4fb] border border-[#3498db]/20 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-semibold text-[#2c3e50]">{selectedVehicle.name}</p>
                          {selectedVehicle.capacity && <p className="text-xs text-[#7f8c8d]">Capacity: {selectedVehicle.capacity}</p>}
                        </div>
                        <button type="button" onClick={() => { setSelectedVehicle(null); setFormData(prev => ({ ...prev, vehicleType: '' })); }}
                          className="text-sm text-[#3498db] hover:underline">Change</button>
                      </div>
                    </div>
                  )}

                  {formData.vehicleType === 'other' && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Please Specify Vehicle Type *</label>
                      <input type="text" name="customVehicleType" value={formData.customVehicleType} onChange={handleChange}
                        className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.customVehicleType ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`}
                        placeholder="Enter vehicle type" />
                      {errors.customVehicleType && <p className="text-[#e74c3c] text-xs mt-1">{errors.customVehicleType}</p>}
                    </div>
                  )}
                </div>
              </div>

              {/* Cargo Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#2c3e50] flex items-center gap-2 mb-4">
                  <FaWeight className="text-[#3498db]" /> Cargo Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Booking Date *</label>
                    <input type="text" value={bookingDateDisplay} onChange={handleBookingDateChange} disabled
                      className="w-full border border-[#e9ecef] rounded-lg px-4 py-2.5 text-sm text-[#7f8c8d] bg-[#f8f9fa] cursor-not-allowed" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Delivery Date *</label>
                    <input type="text" value={deliveryDateDisplay} onChange={handleDeliveryDateChange} placeholder="DD/MM/YYYY"
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.deliveryDate ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.deliveryDate && <p className="text-[#e74c3c] text-xs mt-1">{errors.deliveryDate}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Cargo Weight (kg) *</label>
                    <input type="number" name="cargoWeight" value={formData.cargoWeight} placeholder="e.g: 500" onChange={handleChange} min="1"
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.cargoWeight ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.cargoWeight && <p className="text-[#e74c3c] text-xs mt-1">{errors.cargoWeight}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Cargo Type *</label>
                    <input type="text" name="cargoType" value={formData.cargoType} placeholder="e.g., Electronics, Furniture" onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.cargoType ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.cargoType && <p className="text-[#e74c3c] text-xs mt-1">{errors.cargoType}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Cargo Description</label>
                    <textarea name="description" value={formData.description} onChange={handleChange} rows="1"
                      className="w-full border border-[#e9ecef] focus:border-[#3498db] rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors resize-none"
                      placeholder="Describe your cargo details" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">No. of Vehicles *</label>
                    <input type="number" name="numberOfVehicles" value={formData.numberOfVehicles} placeholder="Number of containers/vehicles" onChange={handleChange} min="1"
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.numberOfVehicles ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.numberOfVehicles && <p className="text-[#e74c3c] text-xs mt-1">{errors.numberOfVehicles}</p>}
                  </div>
                </div>
              </div>

              {/* Location Details */}
              <div>
                <h3 className="text-sm font-semibold text-[#2c3e50] flex items-center gap-2 mb-4">
                  <FaMapMarkerAlt className="text-[#3498db]" /> Location Details
                </h3>
                <div className="space-y-4">
                  <LocationSelect label="Pickup Location *" name="pickupLocation" value={formData.pickupLocation}
                    onChange={handleLocationChange} placeholder="Search for pickup location..." error={errors.pickupLocation} />
                  <button type="button" onClick={() => handleOpenMapSelector('pickup')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#f8f9fa] border border-[#e9ecef] rounded-lg text-sm text-[#7f8c8d] hover:border-[#3498db] hover:text-[#3498db] transition-colors">
                    <FaMapMarkerAlt /> Select on Map
                  </button>
                  <LocationSelect label="Drop Location *" name="dropLocation" value={formData.dropLocation}
                    onChange={handleLocationChange} placeholder="Search for drop location..." error={errors.dropLocation} />
                  <button type="button" onClick={() => handleOpenMapSelector('drop')}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#f8f9fa] border border-[#e9ecef] rounded-lg text-sm text-[#7f8c8d] hover:border-[#3498db] hover:text-[#3498db] transition-colors">
                    <FaMapMarkerAlt /> Select on Map
                  </button>
                </div>
              </div>

              {/* Company Information */}
              <div>
                <h3 className="text-sm font-semibold text-[#2c3e50] flex items-center gap-2 mb-4">
                  <FaBuilding className="text-[#3498db]" /> Company Information
                </h3>
                <div>
                  <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Company Name</label>
                  <input type="text" name="companyName" value={formData.companyName} onChange={handleChange}
                    className="w-full border border-[#e9ecef] focus:border-[#3498db] rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors"
                    placeholder="Enter your company name" />
                </div>
              </div>

              {/* Additional Options */}
              <div>
                <h3 className="text-sm font-semibold text-[#2c3e50] flex items-center gap-2 mb-3">
                  <FaList className="text-[#3498db]" /> Additional Options
                </h3>
                <label className="flex items-center gap-2 text-sm text-[#2c3e50] cursor-pointer">
                  <input type="checkbox" name="salesTax" checked={formData.salesTax} onChange={handleChange}
                    className="w-4 h-4 rounded border-[#e9ecef] text-[#3498db] focus:ring-[#3498db]" />
                  Sales Tax Invoice
                </label>
              </div>
            </div>
          </div>

            <div className="flex justify-end gap-3">
              <button type="submit" disabled={loading}
                className="bg-[#3498db] hover:bg-[#2980b9] text-white font-semibold px-6 py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating…</>
                ) : 'Create Shipment'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-5">
            {/* Customer Information for clearance */}
            <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-[#e9ecef] flex items-center gap-2">
                <FaUser className="text-[#3498db]" />
                <h2 className="text-base font-semibold text-[#2c3e50]">Customer Information</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Full Name *</label>
                    <input type="text" name="name" value={formData.name} placeholder="Enter your full name" onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.name ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.name && <p className="text-[#e74c3c] text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Email *</label>
                    <input type="email" name="email" value={formData.email} placeholder="Enter your email" onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.email ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.email && <p className="text-[#e74c3c] text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Phone *</label>
                    <input type="text" name="phone" value={formData.phone} placeholder="Enter your phone number" onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.phone ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.phone && <p className="text-[#e74c3c] text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5">Company *</label>
                    <input type="text" name="company" value={formData.company} placeholder="Enter company name" onChange={handleChange}
                      className={`w-full border rounded-lg px-4 py-2.5 text-sm text-[#2c3e50] focus:outline-none transition-colors ${errors.company ? 'border-[#e74c3c]' : 'border-[#e9ecef] focus:border-[#3498db]'}`} />
                    {errors.company && <p className="text-[#e74c3c] text-xs mt-1">{errors.company}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Clearance Tabs */}
            <AdminClearanceTabs 
              customerInfo={{ name: formData.name, email: formData.email, phone: formData.phone, company: formData.company}} 
              onResponse={(resp) => {
                if (resp.userCreated && resp.tempPassword) {
                  setTempPassword(resp.tempPassword)
                  setShowPasswordModal(true)
                  } else if (resp.submitted) {
                    // After final submission, navigate to clearance list
                    navigate('/clearance')
                  }
                }}
            />
          </div>
        )}

        {/* Password Modal */}
        {showPasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPasswordModal(false)}>
            <div className="bg-white rounded-lg w-full max-w-[480px] shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="bg-[#2c3e50] text-white px-6 py-5 rounded-t-lg flex items-center gap-2">
                <FaCheckCircle />
                <h3 className="text-base font-semibold">New Customer Created</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-[#7f8c8d] mb-4">
                  A new customer account has been created. Share this temporary password with the client:
                </p>
                <div className="bg-[#f8f9fa] border border-[#e9ecef] rounded-lg p-4 font-mono text-base text-center text-[#2c3e50]">
                  {tempPassword}
                </div>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => { setShowPasswordModal(false); navigate(mode === 'clearance' ? '/clearance' : '/orders'); }}
                    className="px-6 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg transition-colors text-sm font-semibold">
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <VehicleTypeSelector
          isOpen={selectorOpen}
          onClose={() => setSelectorOpen(false)}
          onSelect={handleVehicleSelect}
          category={selectedCategory}
        />

        <LocationMapSelector
          isOpen={mapSelectorOpen.pickup || mapSelectorOpen.drop}
          onClose={handleCloseMapSelector}
          onSelect={handleMapLocationSelect}
          locationType={mapSelectorType}
          currentAddress={mapSelectorType === 'pickup' ? formData.pickupLocation : formData.dropLocation}
        />
      </div>
    </div>
  );
};

export default AdminCreateShipment;